// Platform-admin "Adjust credits" helpers (ADM-R01, CLAUDE.md §3.6). Pure
// module so the form parsing and error mapping are unit-testable.

export type CreditAdjustTarget = 'user' | 'school';
export type CreditAdjustType = 'purchased' | 'subscription';

export interface CreditAdjustForm {
  target: CreditAdjustTarget;
  targetId: string;
  delta: string;
  creditType: CreditAdjustType;
  note: string;
}

/** Body of POST /api/credits/admin/adjust: exactly one of user_id / school_id. */
export interface CreditAdjustPayload {
  user_id?: number;
  school_id?: number;
  delta: number;
  credit_type: CreditAdjustType;
  note?: string;
}

export type CreditAdjustParse =
  | { ok: true; payload: CreditAdjustPayload }
  | { ok: false; errorKey: string };

const POSITIVE_ID = /^[1-9]\d{0,9}$/;
const SIGNED_INT = /^[+-]?\d{1,6}$/;

/** Validates the form and builds the request body, or names the i18n error key. */
export function parseCreditAdjustForm(form: CreditAdjustForm): CreditAdjustParse {
  const targetId = (form.targetId || '').trim();
  if (!POSITIVE_ID.test(targetId)) {
    return { ok: false, errorKey: 'platformAdmin.creditAdjust.invalidTarget' };
  }
  const deltaText = (form.delta || '').trim();
  const delta = SIGNED_INT.test(deltaText) ? Number(deltaText) : NaN;
  if (!Number.isInteger(delta) || delta === 0) {
    return { ok: false, errorKey: 'platformAdmin.creditAdjust.invalidDelta' };
  }
  const payload: CreditAdjustPayload = {
    delta,
    credit_type: form.creditType,
    ...(form.target === 'user' ? { user_id: Number(targetId) } : { school_id: Number(targetId) }),
  };
  const note = (form.note || '').trim();
  if (note) payload.note = note;
  return { ok: true, payload };
}

/** i18n key for a failed adjustment, by HTTP status. */
export function creditAdjustErrorKey(status?: number): string {
  if (status === 400 || status === 422) return 'platformAdmin.creditAdjust.rejected';
  if (status === 403) return 'platformAdmin.creditAdjust.forbidden';
  return 'platformAdmin.creditAdjust.error';
}
